import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import type { NormalizedIdeaCardData } from '@/lib/idea-card-types';
import { getMarkdownIdeaComponents } from './markdown-components';
import { formatIdeaCardBodyForMarkdown, getIdeaCardPresentation } from './presentation';
import { FormatTypeRenderer } from '../FormatTypeMockups/FormatTypeRenderer';
import { linkifyIdeaCardMarkdown } from '@/lib/idea-card-text';

interface IdeaCardProps {
  card: NormalizedIdeaCardData;
  markdownComponents: ReturnType<typeof getMarkdownIdeaComponents>;
  className?: string;
  bodyClassName?: string;
}

export const IdeaCard: React.FC<IdeaCardProps> = ({
  card,
  markdownComponents,
  className = '',
  bodyClassName = '',
}) => {
  const presentation = React.useMemo(
    () => getIdeaCardPresentation(card.layout),
    [card.layout]
  );
  const displayBody = React.useMemo(
    () => linkifyIdeaCardMarkdown(formatIdeaCardBodyForMarkdown(card.displayBody)),
    [card.displayBody]
  );
  const displayBodySections =
    card.bodyModel === 'sectioned_horizontal'
      ? card.displayBodySections
      : [];
  const shouldRenderBody = Boolean(displayBody) && card.bodyMode !== 'hidden';
  const shouldRenderSectionedBody =
    card.bodyMode !== 'hidden' &&
    card.bodyModel === 'sectioned_horizontal' &&
    displayBodySections.length > 0;

  return (
    <Card className={`bg-white/80 dark:bg-neutral-900/80 border-none p-0 ${className}`}>
      <CardHeader className="relative space-y-0 p-3 md:p-3 pb-2" dir="auto">
        {card.layout === 'horizontal' && card.displayTitle && (
          <h3 className="font-bold text-base text-neutral-900 dark:text-white leading-tight mb-1">
            {card.displayTitle}
          </h3>
        )}
      </CardHeader>
      <CardContent
        className={`px-3 md:px-3 ${card.layout === 'horizontal' && card.displayTitle ? 'pt-0' : 'pt-2'} text-sm text-neutral-800 dark:text-white break-words ${presentation.cardContentClassName} ${bodyClassName}`}
        dir="auto"
      >
        {/* Render format type mockup if present */}
        {card.preview && (
          <FormatTypeRenderer preview={card.preview} />
        )}

        {/* Render body content */}
        {shouldRenderSectionedBody && (
          <div className="space-y-4">
            {displayBodySections.map((section, index) => (
              <div key={`${section.displayLabel || section.label}-${index}`} className="space-y-1.5">
                {(section.displayLabel || section.label) && (
                  <p className="text-xs font-semibold tracking-wide text-neutral-500 dark:text-neutral-400">
                    {(section.displayLabel || section.label || '').trim()}:
                  </p>
                )}
                <ReactMarkdown
                  className={`prose dark:prose-invert prose-sm max-w-none text-inherit ${presentation.markdownClassName} prose-p:my-1`}
                  components={markdownComponents}
                  remarkPlugins={[remarkGfm]}
                >
                  {linkifyIdeaCardMarkdown(
                    formatIdeaCardBodyForMarkdown(section.displayContent || section.content)
                  )}
                </ReactMarkdown>
              </div>
            ))}
          </div>
        )}

        {!shouldRenderSectionedBody && shouldRenderBody && (
          <ReactMarkdown
            className={`prose dark:prose-invert prose-sm max-w-none text-inherit ${presentation.markdownClassName}`}
            components={markdownComponents}
            remarkPlugins={[remarkGfm]}
          >
            {displayBody}
          </ReactMarkdown>
        )}
      </CardContent>
    </Card>
  );
};
